import React, { ForwardRefRenderFunction, forwardRef, useImperativeHandle, useMemo, useRef } from "react";
//#region types
import { SupplierFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { FormFieldConstants, ServiceConstants } from "utils/constants";
import { SupplierSettingFormContainerProps } from "models/propTypes/supplierPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormikProps } from "formik";
//#endregion
//#region  components
import SupplierSettingsForm from "./settingsForm";
import {
  defaultPeriodisationSchema,
  getDefaultPeriodisationSaveFailedMessage,
  toDefaultPeriodisationFormValue,
} from "./defaultPeriodisationFormValue";
import { useSupplierService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance, ServiceHelper } from "utils/helpers";

// An unparseable value would be sent as null and fail the whole save, so the stored value is sent instead; the API
// keeps stored months whenever the pair it gets is invalid.
const toWholeNumber = (value: unknown, fallback: number) => {
  const parsed = value === "" || value === undefined || value === null ? NaN : Number(value);
  return Number.isInteger(parsed) ? parsed : fallback;
};

const SupplierSettingsFormContainer: ForwardRefRenderFunction<FormHandles, SupplierSettingFormContainerProps> = (
  props,
  ref
) => {
  const supplierService = useSupplierService();
  const { supplier } = props;
  const formRef = useRef<FormHandles>(null);

  useImperativeHandle(ref, () => ({
    ...formRef.current!,
    submit: (submitFunc, validationSchema, noValidation, data, customValidationFailMessage, ...rest) =>
      formRef.current!.submit(
        submitFunc,
        validationSchema,
        noValidation,
        data,
        customValidationFailMessage ??
          getDefaultPeriodisationSaveFailedMessage(formRef.current!.getValues?.()?.defaultPeriodisation),
        ...rest
      ),
  }));

  const [rules, setRules] = React.useState<WintSuperkollAPIModel.IncomingInvoiceRule[]>();

  React.useEffect(() => {
    ServiceHelper.getAllPages((page: number) =>
      supplierService.getRules(supplier.id, { ...ServiceConstants.DefaultFilter, page: page })
    ).then((rules) => setRules(rules));
  }, [supplier.id]);

  const initialValues = useMemo(
    (): SupplierFormTypes.Settings => ({
      id: supplier.id,
      supplierName: supplier.name ?? "",
      supplierOrgNr: supplier.orgNr ?? "",
      paymentTerms: supplier.paymentTerms,
      // Left undefined when the API does not return the setting, so the field stays hidden instead of
      // showing a default it cannot save.
      defaultPeriodisation: toDefaultPeriodisationFormValue(supplier.defaultPeriodisation),
      pg: supplier.pg ?? supplier.bg,
      street1: supplier.street1,
      street2: supplier.street2,
      city: supplier.city,
      zipCode: supplier.zipCode,
      country: supplier.countryCode,
      countryName: supplier.countryCode ? LocalizationHelperInstance.getFormattedMessage(supplier.countryCode) : "",
      rules: rules ?? [],
      amounts_bookingAccountName: "",
      rulePeriod_dates_dimensions: "",
    }),
    [rules]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object<SupplierFormTypes.Settings>({
        supplierName: Yup.string(),
        supplierOrgNr: Yup.string(),
        pg: Yup.string(),
        paymentTerms: Yup.number().typeError(TranslationKeys.Validations.InvalidPaymentTerms),
        defaultPeriodisation: defaultPeriodisationSchema,
        id: Yup.number(),
        street1: Yup.string(),
        street2: Yup.string(),
        city: Yup.string(),
        zipCode: Yup.string(),
        country: Yup.string(),
        countryName: Yup.string(),
        rules: Yup.array(),
        amounts_bookingAccountName: Yup.string(),
        rulePeriod_dates_dimensions: Yup.mixed(),
      }),
    []
  );

  const handleSubmit = (values: SupplierFormTypes.Settings) => {
    return supplierService.updateSupplier(supplier.id, {
      paymentTerms: values.paymentTerms,
      defaultPeriodisation: values.defaultPeriodisation && {
        enabled: !!values.defaultPeriodisation.isEnabled,
        startOffset: toWholeNumber(values.defaultPeriodisation.from, supplier.defaultPeriodisation?.startOffset ?? 0),
        length: toWholeNumber(values.defaultPeriodisation.duration, supplier.defaultPeriodisation?.length ?? 1),
        appliesBelowMinAmount: !!supplier.defaultPeriodisation?.appliesBelowMinAmount,
      },
      street1: values.street1,
      street2: values.street2,
      city: values.city,
      zipCode: values.zipCode,
      countryCode: values.country,
    });
  };

  const onToggleRuleActivation = async (
    rowIndex: number,
    row: WintSuperkollAPIModel.IncomingInvoiceRule,
    formikProps: FormikProps<SupplierFormTypes.Settings>
  ) => {
    const { values, setFieldValue } = formikProps;
    await supplierService.updateRule(row.id, { ...row, active: !row.active });
    const rules = values.rules ? [...values.rules] : [];
    const rule = rules.find((r) => r.id === row.id);
    if (rule) rule.active = !rule.active;

    setFieldValue(FormFieldConstants.Supplier.Settings.Rules, rules);
  };

  return (
    <SupplierSettingsForm
      ref={formRef}
      {...props}
      validationSchema={validationSchema}
      defaultValues={initialValues}
      handleSubmit={handleSubmit}
      supplierId={supplier.id}
      periodisationAppliesBelowMinAmount={supplier.defaultPeriodisation?.appliesBelowMinAmount}
      onToggleRuleActivation={onToggleRuleActivation}
      onFormValidationChange={props.onFormValidationChange}
    />
  );
};

export default forwardRef(SupplierSettingsFormContainer);
